import React from "react";
import { Link, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { getPost } from "../../actions";
import parse from "html-react-parser";
import "./styles.scss";
import { useEffect, useState, useCallback } from "react";
import { getPresignedUrlView } from "../../utilities/files";

const Article = ({ getPost, post, loading, error }) => {
  const location = useLocation();
  const postID = location.pathname.split("/")[2];
  const [presignedUrl, setPresignedUrl] = useState(null);
  const [imageLoadingState, setImageLoadingState] = useState('loading');
  const [contentReady, setContentReady] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [loadingStage, setLoadingStage] = useState('api');
  const [showImageImmediately, setShowImageImmediately] = useState(false);
  const [imagePreloadComplete, setImagePreloadComplete] = useState(false);
  const [currentPostId, setCurrentPostId] = useState(postID);

  useEffect(() => {
    if (postID !== currentPostId) {
      
      setPresignedUrl(null);
      setImageLoadingState('loading');
      setImageError(false);
      setRetryCount(0);
      setShowImageImmediately(false);
      setImagePreloadComplete(false);
      setContentReady(false);
      setLoadingStage('api');
      setCurrentPostId(postID);
      
    }
  }, [postID, currentPostId]);

  const parsedContent = post.text ? parse(String(post.text)) : '';

  const coverPhoto = post?.coverPhoto;
  const isSpecialPost = post?.title?.includes("Във връзка с отнемане") || 
                       post?.title?.includes("Информация от Румънския");

  // REMOVED: Memoization - calculate final image URL directly
  const getFinalImageUrl = () => {
    // CRITICAL: Always check if we're dealing with the current post
    const isCurrentPost = post?._id === postID;
    
    if (!isCurrentPost) {
      return require("../../assets/imgs/111.PNG");
    }
    
    if (isSpecialPost) {
      return require("../../assets/imgs/111.PNG");
    }
    
    // IMMEDIATELY SHOW IMAGE - even during loading
    // If we have a presigned URL and it's complete, use it
    if (presignedUrl && imagePreloadComplete && isCurrentPost) {
      return presignedUrl;
    }
    
    // Otherwise, show the original coverPhoto immediately (even if it needs presigning)
    if (coverPhoto && isCurrentPost) {
      return coverPhoto;
    }
    
    // Fallback to default
    return require("../../assets/imgs/111.PNG");
  };

  const finalImageUrl = getFinalImageUrl();

  const preloadImage = useCallback(async (imageUrl, expectedPostId) => {
    return new Promise((resolve, reject) => {
      if (expectedPostId !== postID) {
        reject(new Error('Post changed during preload'));
        return;
      }
      
      const img = new Image();
      img.onload = () => {
        if (expectedPostId === postID) {
          resolve(imageUrl);
        } else {
          reject(new Error('Post changed during image load'));
        }
      };
      img.onerror = () => reject(new Error(`Failed to load image: ${imageUrl}`));
      img.src = imageUrl;
    });
  }, [postID]);

  const handleImageRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    setImageError(false);
    setImageLoadingState('loading');
    setImagePreloadComplete(false);
    setPresignedUrl(null); 
  }, []);

  

  useEffect(() => {
    setLoadingStage('api');
    setContentReady(false);
    getPost(postID);
  }, [postID, getPost]);

  useEffect(() => {
    let isMounted = true;
    const expectedPostId = postID; 
    
    const fetchPresignedUrl = async () => {
      if (!isMounted || expectedPostId !== postID) {
        return;
      }
      const currentCoverPhoto = post?.coverPhoto;
      const currentPostDataId = post?._id;
      
      if (!currentCoverPhoto) {
        setImageLoadingState('error');
        setContentReady(true);
        setShowImageImmediately(true);
        setImagePreloadComplete(true);
        return;
      }
      
      if (currentPostDataId && currentPostDataId !== expectedPostId) {
        return;
      }
      
      setLoadingStage('image');
      setImageLoadingState('loading');
      setImagePreloadComplete(false);
      setPresignedUrl(null); 
      
      try {
        if (currentCoverPhoto?.startsWith("https://amarant2.s3") || 
            currentCoverPhoto?.startsWith("https://amarant-rpa.s3") || 
            currentCoverPhoto?.startsWith("https://amarant-docs.s3")) {
          
          const url = await getPresignedUrlView(currentCoverPhoto);
          if (!isMounted || expectedPostId !== postID) {
            return;
          }
          
          try {
            await preloadImage(url, expectedPostId);
            
            if (isMounted && expectedPostId === postID) {
              setPresignedUrl(url);
              setImagePreloadComplete(true);
              setImageLoadingState('loaded');
              setShowImageImmediately(false);
            } else {
            }
          } catch (preloadError) {
            if (isMounted && expectedPostId === postID) {
              console.warn('⚠️ Image preload failed, using fallback:', preloadError);
              setPresignedUrl(currentCoverPhoto);
              setImagePreloadComplete(true);
              setImageLoadingState('loaded');
              setShowImageImmediately(true);
            }
          }
        } else {
          
          try {
            await preloadImage(currentCoverPhoto, expectedPostId);
            
            if (isMounted && expectedPostId === postID) {
              setPresignedUrl(currentCoverPhoto);
              setImagePreloadComplete(true);
              setImageLoadingState('loaded');
              setShowImageImmediately(true);
            }
          } catch (preloadError) {
            if (isMounted && expectedPostId === postID) {
              console.warn('⚠️ Direct image preload failed:', preloadError);
              setImageError(true);
              setImageLoadingState('error');
              setPresignedUrl(require("../../assets/imgs/111.PNG"));
              setImagePreloadComplete(true);
              setShowImageImmediately(true);
            }
          }
        }
      } catch (error) {
        console.error(`❌ Error fetching presigned URL for article ${expectedPostId}:`, error);
        if (isMounted && expectedPostId === postID) {
          setRetryCount(prev => prev + 1);
          setImageError(true);
          setImageLoadingState('error');
          setPresignedUrl(currentCoverPhoto || require("../../assets/imgs/111.PNG"));
          setImagePreloadComplete(true);
          setShowImageImmediately(true);
        }
      }
    };

    const currentCoverPhoto = post?.coverPhoto;
    const currentPostDataId = post?._id;
    
    if (currentCoverPhoto && currentPostDataId === expectedPostId) {
      fetchPresignedUrl();
    } else if (!currentCoverPhoto && currentPostDataId === expectedPostId) {
      setImageLoadingState('error');
      setImagePreloadComplete(true);
      setShowImageImmediately(true);
    }
    
    return () => {
      isMounted = false;
    };
  }, [postID, post?._id, post?.coverPhoto, retryCount, preloadImage]);

  useEffect(() => {
    if (post.title && post._id === postID && (imageLoadingState === 'loaded' || imageLoadingState === 'error' || !post?.coverPhoto)) {
      setLoadingStage('ready');
      setContentReady(true);
    }
  }, [post.title, post._id, post?.coverPhoto, postID, imageLoadingState]);

  

  if (loading && !post.title) {
    return (
      <div className="article-simple-loader">
        <div className="simple-loading-content">
        </div>
      </div>
    );
  }

  if (error && !post.title) {
    return (
      <div className="article-simple-loader">
        <div className="simple-error-content">
          <div className="error-icon">⚠️</div>
          <h3>Възникна грешка</h3>
          <p>Не можахме да заредим статията. Моля, опитайте отново.</p>
          <button 
            onClick={() => {
              setContentReady(false);
              setLoadingStage('api');
              getPost(postID);
            }}
            className="simple-retry-button"
          >
            Опитай отново
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {window.innerWidth < 900 ? (
        <div className="mobile-article-container fade-in">
          <div 
            className={`mobile-right-article-container ${imageLoadingState}`}
            style={{ 
              backgroundImage: `url(${finalImageUrl})`,
              // Remove transition delay - we want immediate display
              transitionDelay: '0s'
            }}
          >
            {/* NO MORE OVERLAY - image shows immediately with blur */}
          </div>
          
          <Link to="/blog">
            <div className="arrow-left mobile" style={{ marginBottom: "5%" }} />
          </Link>

          <h3 
            className="article-title-mobile fade-in-delayed"
            style={{ marginLeft: "2%", marginBottom: "5%" }}
          >
            {post.title}
          </h3>
          
          <div 
            className="mobile-neshto fade-in-content" 
            style={{ paddingBottom: "50px", marginLeft: "3%" }}
          >
            {parsedContent}
          </div>
        </div>
      ) : (
        <div className="article-container fade-in">
          <div className="left-article-container">
            <Link to="/blog">
              <div className="arrow-left" />
            </Link>

            <h1 
              className="article-title fade-in-delayed" 
              style={{ marginTop: "4%" }}
            >
              {post.title}
            </h1>
            
            <div className="neshto">
              <div className="article-text fade-in-content">
                {parsedContent}
              </div>
            </div>
          </div>
          
          <div className="right-image-container">
            <img 
              className={`right-article-container ${imageLoadingState}`}
              src={finalImageUrl} 
              alt={post.title || "Article image"}
              style={{
                // Remove transition delay - we want immediate display
                transitionDelay: '0s'
              }}
              onLoad={() => {
                if (imageLoadingState !== 'loaded' && post?._id === postID) {
                  setImageLoadingState('loaded');
                }
              }}
              onError={() => {
                if (post?._id === postID) {
                  if (retryCount < 2) {
                    handleImageRetry();
                  } else {
                    setImageLoadingState('error');
                  }
                }
              }}
            />
            
            {/* NO MORE OVERLAY - image shows immediately with blur */}
          </div>
        </div>
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  post: state.blog.post,
  loading: state.blog.loading,
  error: state.blog.error,
});

const mapDispachToProps = (dispatch) => ({
  getPost: (payload) => dispatch(getPost(payload)),
});

export default connect(mapStateToProps, mapDispachToProps)(Article);
